'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { Button } from '@repo/studio-design-system-ui-button'

import { Link } from '../navigation'
import { LanguageSwitcher } from './language-switcher'
import { DesktopBrand } from './desktop-brand'

/** Downward "save" arrow used on the header download pill (from the mockup). */
function DownloadArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
    </svg>
  )
}

export function DesktopHeaderRoot({ children }: { children: React.ReactNode }) {
  return (
    <div data-desktop-marketing data-studio-theme="dark">
      <div className="border-b border-sd-border bg-sd-surface backdrop-blur-[14px]">
        <div className="mx-auto flex max-w-[1120px] items-center justify-between gap-4 px-5 py-[18px] md:px-10">
          {children}
        </div>
      </div>
    </div>
  )
}

export function DesktopHeaderBrand() {
  return (
    <Link href="/" aria-label="idea.ai Desktop" className="flex shrink-0 items-center">
      <DesktopBrand />
    </Link>
  )
}

export function DesktopHeaderEnd({ children }: { children: React.ReactNode }) {
  return <div className="ml-auto flex items-center">{children}</div>
}

export function DesktopHeaderNav({ children }: { children?: React.ReactNode }) {
  const t = useTranslations('studio-desktop-landing-pages.lps.shared.nav')

  return (
    <nav aria-label="Primary" className="flex items-center gap-[18px]">
      <Link
        href="/#tools"
        className="hidden text-[14px] font-medium text-sd-slate transition-colors hover:text-sd-ink md:inline"
      >
        {t('tools')}
      </Link>
      <Link
        href="/#how"
        className="hidden text-[14px] font-medium text-sd-slate transition-colors hover:text-sd-ink md:inline"
      >
        {t('how')}
      </Link>
      <Link
        href="/#privacy"
        className="hidden text-[14px] font-medium text-sd-slate transition-colors hover:text-sd-ink md:inline"
      >
        {t('privacy')}
      </Link>
      {children}
      <Button asChild variant="overlay" size="sm" className="rounded-full">
        <Link href={{ pathname: '/thank-you' }}>
          <DownloadArrowIcon className="size-[15px]" />
          {t('download')}
        </Link>
      </Button>
    </nav>
  )
}

export const DesktopHeader = {
  Root: DesktopHeaderRoot,
  Brand: DesktopHeaderBrand,
  End: DesktopHeaderEnd,
  Nav: DesktopHeaderNav,
  LanguageSwitcher,
}

/**
 * Next.js marketing header: sticky bar with brand, section links, language
 * picker, and download CTA. `Nav` accepts children so another host can compose
 * the same chrome without the picker.
 */
export function SiteHeader() {
  return (
    <header className="sticky top-0 z-50">
      <DesktopHeader.Root>
        <DesktopHeader.Brand />
        <DesktopHeader.End>
          <DesktopHeader.Nav>
            <DesktopHeader.LanguageSwitcher />
          </DesktopHeader.Nav>
        </DesktopHeader.End>
      </DesktopHeader.Root>
    </header>
  )
}
